import React from 'react';
import qs from 'query-string';
import { matchPath, useLocation } from 'react-router-dom';
import {
  IconHelpAltActive,
  ButtonIcon,
} from '@spotify-internal/encore-creator-web';
import {
  gray7,
  white,
  black,
  gray20,
  gray90,
} from '@spotify-internal/encore-foundation';
import styled from 'styled-components';
import { sendEvent } from '@mrkt/features/Platform/googleAnalytics';

function buttonBackground(color: string, padding: string) {
  /** css hack for button background color (to remove inner circle) */
  return `
    box-shadow:
      0 0 0 ${padding} ${color},
      inset 0 0 0 8px ${color},
      4px 7px 4px rgba(0, 0, 0, .3);
    position: relative;

    // removes the inner halo
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: -1px;
      bottom: -1px;
      left: -1px;
      right: -1px;
      border-radius: 50%;
      border: 3px solid ${color};
      padding: 0;
    }
  `;
}

const StyledHelpButton = styled(ButtonIcon)`
  color: ${({ theme }) => (theme.app === 'dark' ? gray90 : gray7)};
  background: ${({ theme }) => (theme.app === 'dark' ? gray7 : gray90)};
  border-radius: 50%;
  display: inline-flex;

  ${({ theme }) =>
    buttonBackground(theme.app === 'dark' ? gray90 : gray7, '4px')}

  &:hover {
    color: ${({ theme }) => (theme.app === 'dark' ? white : gray20)};
    transform: scale(1.04);
    transition-duration: 33ms;
    transition-property: scale;
    ${({ theme }) =>
      buttonBackground(theme.app === 'dark' ? white : gray20, '4px')}
  }

  &:focus {
    outline: thin dotted
      ${({ theme }) => (theme.app === 'dark' ? white : black)};
    outline-offset: 5px;
  }
`;

const defaultHelpConfig = {
  faqLinkUrl: '/faq/popular',
  visible: false,
};

const routeHelpConfig: { [key: string]: Partial<typeof defaultHelpConfig> } = {
  '/artist/:artistId/home': {
    visible: true,
  },
  '/artist/:artistId/profile': {
    visible: true,
    faqLinkUrl: '/faq/profile',
  },
  '/artist/:artistId/audience': {
    visible: true,
    faqLinkUrl: '/faq/stats',
  },
  '/artist/:artistId/music': {
    visible: true,
    faqLinkUrl: '/faq/music',
  },
  '/team': {
    visible: true,
    faqLinkUrl: '/faq/access',
  },
  '/settings': {
    visible: true,
  },
  '/activity': {
    visible: true,
    faqLinkUrl: '/faq/access',
  },
};

type HelpButtonContextType = {
  visible: boolean;
  setVisible: Function;
  faqLinkUrl: string;
  setFAQLinkUrl: Function;
};

const HelpButtonContext = React.createContext<HelpButtonContextType>(
  {} as HelpButtonContextType,
);

export const useHelpButton = (): HelpButtonContextType => {
  return React.useContext(HelpButtonContext);
};

export const HelpButtonState: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [visible, setVisible] = React.useState(defaultHelpConfig.visible);
  const [faqLinkUrl, setFAQLinkUrl] = React.useState(
    defaultHelpConfig.faqLinkUrl,
  );
  const location = useLocation();

  // update help config when route changes
  React.useEffect(() => {
    const match = matchPath(location.pathname, {
      path: Object.keys(routeHelpConfig),
    });

    const config = match
      ? {
          ...defaultHelpConfig,
          ...(routeHelpConfig[match.path] || {}),
        }
      : defaultHelpConfig;

    setVisible(config.visible);
    setFAQLinkUrl(config.faqLinkUrl);
  }, [location]);

  return (
    <HelpButtonContext.Provider
      value={{
        setVisible,
        visible,
        faqLinkUrl,
        setFAQLinkUrl,
      }}
    >
      {children}
    </HelpButtonContext.Provider>
  );
};

export const HelpButton: React.FC = () => {
  const { visible, faqLinkUrl } = useHelpButton();

  const parsed = qs.parseUrl(faqLinkUrl);
  const [, hash = ''] = faqLinkUrl.split('#');
  const query = parsed.query;
  query.ref = 'help';
  const url = `${parsed.url}?${qs.stringify(query)}${hash ? '#' : ''}${hash}`;

  const openFAQLink = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const titleBits = (document.title || '').split('–');
      const pageTitle = (titleBits.length && titleBits[0].trim()) || '?';

      sendEvent({
        eventCategory: 'Help v1',
        eventAction: `FAQ Link - ${pageTitle}`,
        eventLabel: faqLinkUrl,
      });

      window.open(url, '_blank');
    },
    [faqLinkUrl, url],
  );

  return (
    <>
      {visible && (
        <StyledHelpButton
          onClick={openFAQLink}
          data-testid="help-button"
          as="a"
          href={url}
        >
          <IconHelpAltActive iconSize={32} aria-label="Help" />
        </StyledHelpButton>
      )}
    </>
  );
};
